/**
 * CreateTenantDialog — alta de un cliente desde la consola (OT-0073 §4.3).
 *
 * Duración de la beta y crédito de IA vienen precargados con los defaults de
 * `GET /platform/tenants/defaults` y son editables: cada cliente puede tener
 * su propio acuerdo. El identificador se sugiere desde el nombre hasta que el
 * operador lo edita, y se valida mientras se escribe con la misma regla del
 * backend; topes, correo y unicidad los valida el backend
 * (`TenantProvisioningService`), cuyo error se muestra tal cual.
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { PlatformTenantDefaults } from '../../api/platform'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import { useCreatePlatformTenant, usePlatformTenantDefaults } from '../../hooks/usePlatform'
import { CLIENT_SLUG_MAX_LENGTH, slugIssue, suggestSlug } from '../../lib/platformClients'
import { Dialog } from '../Dialog'

interface CreateTenantDialogProps {
  open: boolean
  onClose: () => void
}

export function CreateTenantDialog({ open, onClose }: CreateTenantDialogProps) {
  const { t } = useTranslation(['platform', 'common'])
  const defaults = usePlatformTenantDefaults()
  return (
    <Dialog open={open} onClose={onClose} title={t('clients.create.title')} size="lg">
      {defaults.data ? (
        // `key` reinicia el formulario cada vez que se abre.
        <CreateTenantForm key={String(open)} defaults={defaults.data} onClose={onClose} />
      ) : (
        <p className="text-sm text-ink-muted">{t('common:actions.loading')}</p>
      )}
    </Dialog>
  )
}

const INPUT =
  'w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
const LABEL = 'mb-1 block text-xs font-medium text-ink'
const HELP = 'mt-1 text-[11px] text-ink-subtle'

export function CreateTenantForm({
  defaults,
  onClose,
}: {
  defaults: PlatformTenantDefaults
  onClose: () => void
}) {
  const { t } = useTranslation(['platform', 'common'])
  const navigate = useNavigate()
  const errorMessage = useApiErrorMessage()
  const create = useCreatePlatformTenant()

  const [displayName, setDisplayName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [adminEmail, setAdminEmail] = useState('')
  const [locale, setLocale] = useState<'es' | 'en'>('es')
  const [trialMonths, setTrialMonths] = useState(String(defaults.trial_months))
  const [creditUsd, setCreditUsd] = useState(defaults.credit_usd)

  const effectiveSlug = slugEdited ? slug : suggestSlug(displayName)
  const issue = effectiveSlug ? slugIssue(effectiveSlug) : null
  const busy = create.isPending
  const canSubmit =
    !busy &&
    displayName.trim() !== '' &&
    effectiveSlug !== '' &&
    issue === null &&
    adminEmail.trim() !== '' &&
    trialMonths !== '' &&
    creditUsd !== ''

  const submit = () => {
    create.mutate(
      {
        slug: effectiveSlug,
        display_name: displayName.trim(),
        admin_email: adminEmail.trim(),
        default_locale: locale,
        trial_months: Number(trialMonths),
        credit_usd: creditUsd,
      },
      {
        onSuccess: (detail) => {
          onClose()
          void navigate({
            to: '/platform/clients/$tenantId',
            params: { tenantId: detail.tenant_id },
          })
        },
      },
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) submit()
      }}
    >
      <p className="mb-4 font-body text-sm text-ink-muted">{t('clients.create.subtitle')}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="client-name" className={LABEL}>
            {t('clients.create.display_name')}
          </label>
          <input
            id="client-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('clients.create.display_name_placeholder')}
            className={INPUT}
            disabled={busy}
            autoFocus
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="client-slug" className={LABEL}>
            {t('clients.create.slug')}
          </label>
          <input
            id="client-slug"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugEdited(true)
              setSlug(e.target.value)
            }}
            aria-invalid={issue !== null}
            aria-describedby="client-slug-help"
            className={`${INPUT} font-mono`}
            disabled={busy}
          />
          <p
            id="client-slug-help"
            className={issue ? 'mt-1 text-[11px] text-critical' : HELP}
          >
            {issue
              ? t(`clients.create.slug_issue.${issue}`, { max: CLIENT_SLUG_MAX_LENGTH })
              : t('clients.create.slug_help')}
          </p>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="client-admin-email" className={LABEL}>
            {t('clients.create.admin_email')}
          </label>
          <input
            id="client-admin-email"
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder={t('clients.create.admin_email_placeholder')}
            className={`${INPUT} font-mono`}
            disabled={busy}
          />
        </div>

        <div>
          <label htmlFor="client-locale" className={LABEL}>
            {t('clients.create.locale')}
          </label>
          <select
            id="client-locale"
            value={locale}
            onChange={(e) => setLocale(e.target.value === 'en' ? 'en' : 'es')}
            className={INPUT}
            disabled={busy}
          >
            <option value="es">{t('common:languages.es')}</option>
            <option value="en">{t('common:languages.en')}</option>
          </select>
        </div>

        <div />

        <div>
          <label htmlFor="client-trial-months" className={LABEL}>
            {t('clients.create.trial_months')}
          </label>
          <input
            id="client-trial-months"
            type="number"
            min={1}
            max={defaults.max_trial_months}
            step={1}
            value={trialMonths}
            onChange={(e) => setTrialMonths(e.target.value)}
            className={`${INPUT} font-mono`}
            disabled={busy}
          />
          <p className={HELP}>
            {t('clients.create.trial_months_help', {
              default: defaults.trial_months,
              max: defaults.max_trial_months,
            })}
          </p>
        </div>

        <div>
          <label htmlFor="client-credit" className={LABEL}>
            {t('clients.create.credit_usd')}
          </label>
          <input
            id="client-credit"
            type="number"
            min={0.01}
            max={defaults.max_credit_usd}
            step={0.01}
            value={creditUsd}
            onChange={(e) => setCreditUsd(e.target.value)}
            className={`${INPUT} font-mono`}
            disabled={busy}
          />
          <p className={HELP}>
            {t('clients.create.credit_usd_help', {
              default: defaults.credit_usd,
              max: defaults.max_credit_usd,
            })}
          </p>
        </div>
      </div>

      {create.error ? (
        <p role="alert" className="mt-4 text-sm text-critical">
          {errorMessage(create.error)}
        </p>
      ) : null}

      <div className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-muted disabled:opacity-50"
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-canvas hover:bg-brand/90 disabled:opacity-50"
        >
          {busy ? t('clients.create.submitting') : t('clients.create.submit')}
        </button>
      </div>
    </form>
  )
}
